import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../auth/session';
import { useAuth } from '../auth/AuthContext';
import type { ChecklistItem, OnboardingState } from './OnboardingBanner';

// ─── Setup Wizard (tutti i verticali) ───────────────────────────────────────
// Wizard dati: ogni schermata inserisce i dati minimi per operare, chiamando
// le stesse API delle pagine reali. Dopo ogni salvataggio → auto-detect.
// Le schermate il cui modulo non è attivo mostrano uno stato "locked" con
// link allo Store: l'item checklist resta aperto, come nel wizard precedente.

type ScreenId =
  | 'fiscal' | 'products' | 'tables' | 'staff' | 'hours' | 'photo' | 'first_content' | 'summary'
  | 'treatments' | 'cabins' | 'stations' | 'operators' | 'memberships' | 'coaches' | 'members';

type ScreenDef = {
  id: ScreenId;
  title: string;
  itemKey?: string;
  module?: string;
  moduleName?: string;
};

function screensFor(category: string): ScreenDef[] {
  const fiscal: ScreenDef = { id: 'fiscal', title: 'Dati fiscali', itemKey: 'fiscal' };
  const hours: ScreenDef = { id: 'hours', title: 'Orari di apertura', itemKey: 'hours' };
  const photo: ScreenDef = { id: 'photo', title: 'Foto del locale', itemKey: 'photo' };
  const summary: ScreenDef = { id: 'summary', title: 'Riepilogo' };

  switch (category) {
    case 'restaurant':
      return [
        fiscal,
        { id: 'products', title: 'Prodotti del menu', itemKey: 'menu', module: 'restaurant-base', moduleName: 'Menu' },
        { id: 'tables', title: 'Tavoli', itemKey: 'tables', module: 'restaurant-tables', moduleName: 'Tavoli' },
        { id: 'staff', title: 'Staff', itemKey: 'staff', module: 'restaurant-staff', moduleName: 'Staff' },
        hours, photo,
        { id: 'first_content', title: 'Primo contenuto', itemKey: 'first_content', module: 'restaurant-reservations', moduleName: 'Prenotazioni' },
        summary,
      ];
    case 'beauty':
      return [
        fiscal,
        { id: 'treatments', title: 'Trattamenti', itemKey: 'treatments', module: 'beauty-base', moduleName: 'Beauty Base' },
        { id: 'cabins', title: 'Cabine', itemKey: 'cabins', module: 'beauty-cabins', moduleName: 'Cabine' },
        { id: 'operators', title: 'Operatori', itemKey: 'operators', module: 'beauty-operators', moduleName: 'Operatori' },
        hours, photo,
        { id: 'first_content', title: 'Primo contenuto', itemKey: 'first_content', module: 'beauty-bookings', moduleName: 'Prenotazioni' },
        summary,
      ];
    case 'gym':
      return [
        fiscal,
        { id: 'memberships', title: 'Abbonamenti', itemKey: 'memberships', module: 'gym-base', moduleName: 'Gym Base' },
        { id: 'coaches', title: 'Coach', itemKey: 'coaches', module: 'gym-coaches', moduleName: 'Coach' },
        { id: 'members', title: 'Soci', itemKey: 'members', module: 'gym-members', moduleName: 'Soci' },
        hours, photo,
        { id: 'first_content', title: 'Primo contenuto', itemKey: 'first_content', module: 'gym-checkins', moduleName: 'Check-in' },
        summary,
      ];
    case 'hair':
      return [
        fiscal,
        { id: 'treatments', title: 'Trattamenti', itemKey: 'treatments', module: 'hair-base', moduleName: 'Hair Base' },
        { id: 'operators', title: 'Operatori', itemKey: 'operators', module: 'hair-base', moduleName: 'Hair Base' },
        { id: 'stations', title: 'Postazioni', itemKey: 'stations', module: 'hair-base', moduleName: 'Hair Base' },
        hours, photo,
        { id: 'first_content', title: 'Primo contenuto', itemKey: 'first_content', module: 'hair-booking', moduleName: 'Prenotazioni' },
        summary,
      ];
    default:
      return [fiscal, hours, photo, { id: 'first_content', title: 'Primo contenuto', itemKey: 'first_content' }, summary];
  }
}

const ROLE_OPTIONS = [
  { id: 1, label: 'Sala' },
  { id: 2, label: 'Cucina' },
  { id: 3, label: 'Bar' },
  { id: 4, label: 'Manager' },
  { id: 5, label: 'Cassa' },
];

const GIORNI = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica'];

async function readError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string; message?: string; code?: string };
    return body.error || body.message || body.code || `HTTP ${res.status}`;
  } catch {
    return `HTTP ${res.status}`;
  }
}

function itemFor(state: OnboardingState, key?: string): ChecklistItem | undefined {
  if (!key) return undefined;
  return state.checklist.find((i) => i.key === key);
}

function DoneTag({ item, detail }: { item?: ChecklistItem; detail: string }) {
  if (!item) return null;
  return (
    <span
      style={{
        fontSize: '0.7rem',
        padding: '0.15rem 0.5rem',
        borderRadius: '1rem',
        background: item.done ? 'var(--sk-box-success-bg)' : 'var(--sk-box-neutral-bg)',
        color: item.done ? 'var(--sk-box-success-fg)' : 'var(--sk-mid)',
        whiteSpace: 'nowrap',
      }}
    >
      {item.done ? '✓ Fatto' : detail}
    </span>
  );
}

function LockedNotice({ moduleName }: { moduleName: string }) {
  return (
    <div
      style={{
        padding: '0.85rem 1rem',
        borderRadius: '0.5rem',
        background: 'var(--sk-box-warning-bg)',
        border: '1px solid var(--sk-box-warning-border)',
        fontSize: '0.85rem',
        color: 'var(--sk-box-warning-fg)',
      }}
    >
      Questa schermata richiede il modulo <strong>{moduleName}</strong>, che non è attivo.
      Puoi attivarlo dallo <Link to="/tenant/store" style={{ color: 'inherit', fontWeight: 600 }}>Store</Link> e
      riprendere il setup in qualsiasi momento.
    </div>
  );
}

function ErrorBox({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <div
      style={{
        padding: '0.6rem 0.85rem',
        borderRadius: '0.5rem',
        background: 'var(--sk-box-danger-bg, rgba(239,68,68,0.12))',
        border: '1px solid var(--sk-box-danger-border, rgba(239,68,68,0.35))',
        fontSize: '0.8rem',
        color: 'var(--sk-box-danger-fg, #fca5a5)',
        marginBottom: '0.75rem',
      }}
    >
      {error}
    </div>
  );
}

const listStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.4rem',
  marginTop: '0.75rem',
};

const listRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '0.5rem',
  padding: '0.5rem 0.75rem',
  borderRadius: '0.5rem',
  background: 'var(--sk-box-neutral-bg)',
  fontSize: '0.85rem',
};

const formRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: '0.5rem',
  flexWrap: 'wrap',
  alignItems: 'flex-end',
  marginTop: '0.75rem',
};

const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.2rem',
  fontSize: '0.75rem',
  color: 'var(--sk-mid)',
};

// ─── Schermata: Dati fiscali ────────────────────────────────────────────────

type FiscalProfile = {
  business_name?: string | null;
  vat_number?: string | null;
  sdi_code?: string | null;
  pec?: string | null;
  legal_address?: string | null;
  legal_city?: string | null;
  legal_zip?: string | null;
  legal_province?: string | null;
};

function FiscalScreen({ refresh }: { refresh: () => Promise<void> }) {
  const [form, setForm] = useState<FiscalProfile>({});
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const res = await apiFetch('/api/v1/tenant/billing/profile');
      if (!res.ok) return;
      const body = (await res.json()) as { profile?: FiscalProfile };
      if (body.profile) setForm(body.profile);
    })();
  }, []);

  function patch(k: keyof FiscalProfile, v: string) {
    setSaved(false);
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!String(form.vat_number || '').trim()) {
      setError('La partita IVA è obbligatoria per proseguire.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch('/api/v1/tenant/billing/profile', {
        method: 'PUT',
        body: JSON.stringify({
          business_name: form.business_name || undefined,
          vat_number: String(form.vat_number).trim(),
          sdi_code: form.sdi_code || null,
          pec: form.pec || null,
          legal_address: form.legal_address || null,
          legal_city: form.legal_city || null,
          legal_zip: form.legal_zip || null,
          legal_province: form.legal_province || null,
        }),
      });
      if (!res.ok) throw new Error(await readError(res));
      setSaved(true);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <p style={{ color: 'var(--sk-mid)', fontSize: '0.85rem', marginTop: 0 }}>
        Servono per la fatturazione elettronica. Solo la partita IVA è obbligatoria in questa fase.
      </p>
      <ErrorBox error={error} />
      <form onSubmit={save} style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginTop: '0.5rem' }}>
        <div style={formRowStyle}>
          <label style={{ ...labelStyle, flex: '1 1 12rem' }}>
            Ragione sociale
            <input className="sk-input" value={form.business_name || ''} onChange={(e) => patch('business_name', e.target.value)} />
          </label>
          <label style={{ ...labelStyle, flex: '0 1 10rem' }}>
            Partita IVA *
            <input className="sk-input" value={form.vat_number || ''} onChange={(e) => patch('vat_number', e.target.value)} />
          </label>
        </div>
        <div style={formRowStyle}>
          <label style={{ ...labelStyle, flex: '0 1 8rem' }}>
            Codice SDI
            <input className="sk-input" value={form.sdi_code || ''} onChange={(e) => patch('sdi_code', e.target.value)} placeholder="0000000" maxLength={7} />
          </label>
          <label style={{ ...labelStyle, flex: '1 1 12rem' }}>
            PEC
            <input className="sk-input" type="email" value={form.pec || ''} onChange={(e) => patch('pec', e.target.value)} />
          </label>
        </div>
        <div style={formRowStyle}>
          <label style={{ ...labelStyle, flex: '1 1 12rem' }}>
            Indirizzo legale
            <input className="sk-input" value={form.legal_address || ''} onChange={(e) => patch('legal_address', e.target.value)} />
          </label>
          <label style={{ ...labelStyle, flex: '0 1 8rem' }}>
            Città
            <input className="sk-input" value={form.legal_city || ''} onChange={(e) => patch('legal_city', e.target.value)} />
          </label>
          <label style={{ ...labelStyle, flex: '0 1 5rem' }}>
            CAP
            <input className="sk-input" value={form.legal_zip || ''} onChange={(e) => patch('legal_zip', e.target.value)} />
          </label>
          <label style={{ ...labelStyle, flex: '0 1 4rem' }}>
            Prov.
            <input className="sk-input" value={form.legal_province || ''} onChange={(e) => patch('legal_province', e.target.value)} maxLength={2} />
          </label>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button className="sk-btn" type="submit" disabled={busy}>
            {busy ? 'Salvataggio…' : 'Salva dati fiscali'}
          </button>
          {saved && <span style={{ fontSize: '0.8rem', color: 'var(--sk-box-success-fg)' }}>✓ Salvato</span>}
        </div>
      </form>
    </div>
  );
}

// ─── Schermata generica quick-add (entità dei verticali) ────────────────────

type QuickField = {
  key: string;
  label: string;
  type?: 'text' | 'number' | 'select';
  options?: Array<{ value: string; label: string }>;
  required?: boolean;
  flex?: string;
  placeholder?: string;
};

type QuickAddConfig = {
  url: string; // GET lista + POST create (+ DELETE /:id se canDelete)
  listKey?: string;
  fields: QuickField[];
  defaults?: Record<string, string>;
  itemLabel: (item: Record<string, unknown>) => string;
  itemId?: (item: Record<string, unknown>) => number | string | undefined;
  target: number;
  unit: string;
  canDelete?: boolean;
  intro: string;
};

function QuickAddScreen({ cfg, refresh }: { cfg: QuickAddConfig; refresh: () => Promise<void> }) {
  const [items, setItems] = useState<Array<Record<string, unknown>>>([]);
  const [form, setForm] = useState<Record<string, string>>({ ...(cfg.defaults || {}) });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const res = await apiFetch(cfg.url);
    if (!res.ok) return;
    const body = (await res.json()) as Record<string, unknown>;
    const list = (cfg.listKey ? body[cfg.listKey] : undefined) ??
      Object.values(body).find((v) => Array.isArray(v));
    if (Array.isArray(list)) setItems(list as Array<Record<string, unknown>>);
  }, [cfg]);

  useEffect(() => { void load(); }, [load]);

  function fieldValue(f: QuickField) {
    return form[f.key] ?? (f.type === 'select' ? f.options?.[0]?.value ?? '' : '');
  }

  async function add(e: FormEvent) {
    e.preventDefault();
    const missing = cfg.fields.find((f) => f.required && !fieldValue(f).trim());
    if (missing) {
      setError(`Il campo "${missing.label}" è obbligatorio.`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = {};
      for (const f of cfg.fields) {
        const v = fieldValue(f).trim();
        if (v === '' && !f.required) continue;
        payload[f.key] = f.type === 'number' ? Number(v) || 0 : v;
      }
      const res = await apiFetch(cfg.url, { method: 'POST', body: JSON.stringify(payload) });
      if (!res.ok) throw new Error(await readError(res));
      setForm({ ...(cfg.defaults || {}) });
      await load();
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: number | string) {
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch(`${cfg.url}/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(await readError(res));
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const canSubmit = cfg.fields.every((f) => !f.required || fieldValue(f).trim().length > 0);

  return (
    <div>
      <p style={{ color: 'var(--sk-mid)', fontSize: '0.85rem', marginTop: 0 }}>
        {cfg.intro} <strong>{items.length}/{cfg.target}</strong> {cfg.unit}
      </p>
      <ErrorBox error={error} />
      <form onSubmit={add} style={formRowStyle}>
        {cfg.fields.map((f) => (
          <label key={f.key} style={{ ...labelStyle, flex: f.flex || '1 1 8rem' }}>
            {f.label}{f.required ? ' *' : ''}
            {f.type === 'select' ? (
              <select className="sk-input" value={fieldValue(f)} onChange={(e) => setForm((prev) => ({ ...prev, [f.key]: e.target.value }))}>
                {(f.options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            ) : (
              <input
                className="sk-input"
                type={f.type === 'number' ? 'number' : 'text'}
                value={fieldValue(f)}
                placeholder={f.placeholder}
                onChange={(e) => setForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
              />
            )}
          </label>
        ))}
        <button className="sk-btn" type="submit" disabled={busy || !canSubmit}>+ Aggiungi</button>
      </form>
      {items.length > 0 && (
        <div style={listStyle}>
          {items.map((item, i) => {
            const id = cfg.itemId ? cfg.itemId(item) : undefined;
            return (
              <div key={String(id ?? i)} style={listRowStyle}>
                <span>{cfg.itemLabel(item)}</span>
                {cfg.canDelete && id != null && (
                  <button type="button" className="sk-btn sk-btn--quiet" onClick={() => void remove(id)} disabled={busy}>✕</button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const BEAUTY_TREATMENTS_CFG: QuickAddConfig = {
  url: '/api/v1/beauty/treatments', listKey: 'treatments', target: 2, unit: 'trattamenti',
  intro: 'Crea i trattamenti offerti dal centro.', canDelete: true,
  fields: [
    { key: 'name', label: 'Nome', required: true, flex: '1 1 10rem', placeholder: 'es. Pulizia viso' },
    { key: 'duration_min', label: 'Durata (min)', type: 'number', flex: '0 1 6rem' },
    { key: 'price', label: 'Prezzo €', type: 'number', flex: '0 1 6rem' },
  ],
  defaults: { duration_min: '60' },
  itemId: (i) => (i.id ?? i.treatment_id) as number | undefined,
  itemLabel: (i) => `${i.name}${i.price != null ? ` · €${Number(i.price).toFixed(2)}` : ''}`,
};

const HAIR_TREATMENTS_CFG: QuickAddConfig = {
  url: '/api/v1/hair/treatments', listKey: 'treatments', target: 1, unit: 'trattamento',
  intro: 'Crea almeno un trattamento del salone.', canDelete: true,
  fields: [
    { key: 'name', label: 'Nome', required: true, flex: '1 1 10rem', placeholder: 'es. Taglio donna' },
    { key: 'duration_min', label: 'Durata (min)', type: 'number', flex: '0 1 6rem' },
    { key: 'price', label: 'Prezzo €', type: 'number', flex: '0 1 6rem' },
  ],
  defaults: { duration_min: '45' },
  itemId: (i) => (i.id ?? i.treatment_id) as number | undefined,
  itemLabel: (i) => `${i.name}${i.price != null ? ` · €${Number(i.price).toFixed(2)}` : ''}`,
};

const BEAUTY_CABINS_CFG: QuickAddConfig = {
  url: '/api/v1/beauty/cabins', listKey: 'cabins', target: 1, unit: 'cabina',
  intro: 'Configura le cabine operative.', canDelete: true,
  fields: [
    { key: 'name', label: 'Nome cabina', required: true, flex: '1 1 10rem', placeholder: 'es. Cabina 1' },
    { key: 'type', label: 'Tipo', type: 'select', flex: '0 1 8rem',
      options: [
        { value: 'general', label: 'Generica' },
        { value: 'massage', label: 'Massaggio' },
        { value: 'facial', label: 'Viso' },
        { value: 'body', label: 'Corpo' },
      ] },
    { key: 'capacity', label: 'Posti', type: 'number', flex: '0 1 5rem' },
  ],
  defaults: { type: 'general', capacity: '1' },
  itemId: (i) => (i.id ?? i.cabin_id) as number | undefined,
  itemLabel: (i) => `${i.name} · ${i.type || 'general'}`,
};

const BEAUTY_OPERATORS_CFG: QuickAddConfig = {
  url: '/api/v1/beauty/operators', listKey: 'operators', target: 1, unit: 'operatore',
  intro: 'Aggiungi gli operatori che erogano i trattamenti.', canDelete: true,
  fields: [
    { key: 'display_name', label: 'Nome', required: true, flex: '1 1 10rem', placeholder: 'es. Sara' },
    { key: 'level', label: 'Livello', type: 'select', flex: '0 1 7rem',
      options: [
        { value: 'junior', label: 'Junior' },
        { value: 'senior', label: 'Senior' },
        { value: 'master', label: 'Master' },
      ] },
  ],
  defaults: { level: 'senior' },
  itemId: (i) => (i.id ?? i.operator_id) as number | undefined,
  itemLabel: (i) => `${i.display_name} · ${i.level || ''}`,
};

const HAIR_OPERATORS_CFG: QuickAddConfig = {
  url: '/api/v1/hair/operators', listKey: 'operators', target: 1, unit: 'operatore',
  intro: 'Aggiungi gli operatori del salone.', canDelete: true,
  fields: [
    { key: 'display_name', label: 'Nome', required: true, flex: '1 1 10rem', placeholder: 'es. Martina' },
  ],
  itemId: (i) => (i.id ?? i.operator_id) as number | undefined,
  itemLabel: (i) => `${i.display_name}`,
};

const HAIR_STATIONS_CFG: QuickAddConfig = {
  url: '/api/v1/hair/stations', listKey: 'stations', target: 1, unit: 'postazione',
  intro: 'Configura le postazioni di lavoro.', canDelete: true,
  fields: [
    { key: 'name', label: 'Nome postazione', required: true, flex: '1 1 10rem', placeholder: 'es. Poltrona 1' },
    { key: 'type', label: 'Tipo', type: 'select', flex: '0 1 8rem',
      options: [
        { value: 'chair', label: 'Poltrona' },
        { value: 'wash', label: 'Lavatesta' },
        { value: 'color', label: 'Colore' },
      ] },
  ],
  defaults: { type: 'chair' },
  itemId: (i) => (i.id ?? i.station_id) as number | undefined,
  itemLabel: (i) => `${i.name} · ${i.type || ''}`,
};

const GYM_MEMBERSHIPS_CFG: QuickAddConfig = {
  url: '/api/v1/gym/memberships', listKey: 'memberships', target: 2, unit: 'abbonamenti',
  intro: 'Crea i tipi di abbonamento venduti dalla palestra.', canDelete: true,
  fields: [
    { key: 'name', label: 'Nome', required: true, flex: '1 1 10rem', placeholder: 'es. Mensile open' },
    { key: 'duration_days', label: 'Durata (gg)', type: 'number', flex: '0 1 6rem' },
    { key: 'price', label: 'Prezzo €', type: 'number', flex: '0 1 6rem' },
  ],
  defaults: { duration_days: '30' },
  itemId: (i) => (i.id ?? i.membership_id) as number | undefined,
  itemLabel: (i) => `${i.name}${i.price != null ? ` · €${Number(i.price).toFixed(2)}` : ''}`,
};

const GYM_COACHES_CFG: QuickAddConfig = {
  url: '/api/v1/gym/coaches', listKey: 'coaches', target: 1, unit: 'coach',
  intro: 'Aggiungi i coach della palestra.', canDelete: true,
  fields: [
    { key: 'display_name', label: 'Nome', required: true, flex: '1 1 10rem', placeholder: 'es. Luca' },
    { key: 'specialization', label: 'Specializzazione', flex: '0 1 10rem', placeholder: 'es. Sala pesi' },
  ],
  itemId: (i) => (i.id ?? i.coach_id) as number | undefined,
  itemLabel: (i) => `${i.display_name}${i.specialization ? ` · ${i.specialization}` : ''}`,
};

const GYM_MEMBERS_CFG: QuickAddConfig = {
  url: '/api/v1/gym/members', listKey: 'members', target: 2, unit: 'soci',
  intro: 'Registra i primi soci della palestra.', canDelete: true,
  fields: [
    { key: 'display_name', label: 'Nome e cognome', required: true, flex: '1 1 10rem', placeholder: 'es. Mario Rossi' },
    { key: 'phone', label: 'Telefono', flex: '0 1 9rem', placeholder: '+39...' },
  ],
  itemId: (i) => (i.id ?? i.member_id) as number | undefined,
  itemLabel: (i) => `${i.display_name}${i.phone ? ` · ${i.phone}` : ''}`,
};

// ─── Schermata: Prodotti ────────────────────────────────────────────────────

const PRODUCT_TARGET = 2;

type MenuItem = { id: number; name: string; price: number | string };
type MenuCategory = { id: number; name: string };

function ProductsScreen({ refresh }: { refresh: () => Promise<void> }) {
  const [items, setItems] = useState<MenuItem[]>([]);
  const [cats, setCats] = useState<MenuCategory[]>([]);
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [catId, setCatId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [menuRes, catRes] = await Promise.all([
      apiFetch('/api/v1/restaurant/menu'),
      apiFetch('/api/v1/restaurant/menu/categories'),
    ]);
    if (menuRes.ok) {
      const body = (await menuRes.json()) as { items?: MenuItem[] };
      setItems(body.items || []);
    }
    if (catRes.ok) {
      const body = (await catRes.json()) as { categories?: MenuCategory[] };
      setCats(body.categories || []);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function add(e: FormEvent) {
    e.preventDefault();
    const n = name.trim();
    const p = Number(String(price).replace(',', '.'));
    if (!n || !Number.isFinite(p) || p < 0) {
      setError('Inserisci nome e prezzo validi.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch('/api/v1/restaurant/menu', {
        method: 'POST',
        body: JSON.stringify({ name: n, price: p, ...(catId ? { category_id: Number(catId) } : {}) }),
      });
      if (!res.ok) throw new Error(await readError(res));
      setName('');
      setPrice('');
      await Promise.all([load(), refresh()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: number) {
    setBusy(true);
    try {
      const res = await apiFetch(`/api/v1/restaurant/menu/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(await readError(res));
      await Promise.all([load(), refresh()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <p style={{ color: 'var(--sk-mid)', fontSize: '0.85rem', marginTop: 0 }}>
        Aggiungi almeno {PRODUCT_TARGET} prodotti per iniziare. Potrai completare il menu dalla pagina dedicata.
      </p>
      <p style={{ fontSize: '0.8rem', color: items.length >= PRODUCT_TARGET ? 'var(--sk-box-success-fg)' : 'var(--sk-mid)' }}>
        {items.length}/{PRODUCT_TARGET} prodotti inseriti
      </p>
      <ErrorBox error={error} />
      <form onSubmit={add} style={formRowStyle}>
        <label style={{ ...labelStyle, flex: '1 1 10rem' }}>
          Nome prodotto
          <input className="sk-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="es. Margherita" />
        </label>
        <label style={{ ...labelStyle, flex: '0 1 6rem' }}>
          Prezzo €
          <input className="sk-input" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="8.50" inputMode="decimal" />
        </label>
        {cats.length > 0 && (
          <label style={{ ...labelStyle, flex: '0 1 9rem' }}>
            Categoria
            <select className="sk-input" value={catId} onChange={(e) => setCatId(e.target.value)}>
              <option value="">—</option>
              {cats.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </label>
        )}
        <button className="sk-btn" type="submit" disabled={busy || !name.trim() || !price.trim()}>
          + Aggiungi
        </button>
      </form>
      {items.length > 0 && (
        <div style={listStyle}>
          {items.slice(0, 10).map((it) => (
            <div key={it.id} style={listRowStyle}>
              <span>{it.name}</span>
              <span style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <span style={{ color: 'var(--sk-mid)' }}>€ {Number(it.price).toFixed(2)}</span>
                <button type="button" className="sk-btn sk-btn--quiet" onClick={() => void remove(it.id)} disabled={busy}>✕</button>
              </span>
            </div>
          ))}
          {items.length > 10 && <span className="sk-muted" style={{ fontSize: '0.75rem' }}>…e altri {items.length - 10}</span>}
        </div>
      )}
    </div>
  );
}

// ─── Schermata: Tavoli ──────────────────────────────────────────────────────

const TABLE_TARGET = 1;

type TableRow = { id: number; table_number: number; seats: number };

function TablesScreen({ refresh }: { refresh: () => Promise<void> }) {
  const [tables, setTables] = useState<TableRow[]>([]);
  const [number, setNumber] = useState('1');
  const [seats, setSeats] = useState('4');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const res = await apiFetch('/api/v1/restaurant/tables');
    if (!res.ok) return;
    const body = (await res.json()) as { tables?: TableRow[] };
    const rows = body.tables || [];
    setTables(rows);
    if (rows.length > 0) {
      setNumber(String(Math.max(...rows.map((t) => Number(t.table_number) || 0)) + 1));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function add(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch('/api/v1/restaurant/tables', {
        method: 'POST',
        body: JSON.stringify({ table_number: Number(number), seats: Number(seats) || 2 }),
      });
      if (!res.ok) throw new Error(await readError(res));
      await Promise.all([load(), refresh()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: number) {
    setBusy(true);
    try {
      const res = await apiFetch(`/api/v1/restaurant/tables/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(await readError(res));
      await Promise.all([load(), refresh()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <p style={{ color: 'var(--sk-mid)', fontSize: '0.85rem', marginTop: 0 }}>
        Configura almeno {TABLE_TARGET} tavolo. Sale, planimetria e dettagli si gestiscono dalla pagina Tavoli.
      </p>
      <p style={{ fontSize: '0.8rem', color: tables.length >= TABLE_TARGET ? 'var(--sk-box-success-fg)' : 'var(--sk-mid)' }}>
        {tables.length}/{TABLE_TARGET} tavoli configurati
      </p>
      <ErrorBox error={error} />
      <form onSubmit={add} style={formRowStyle}>
        <label style={{ ...labelStyle, flex: '0 1 7rem' }}>
          N° tavolo
          <input className="sk-input" type="number" min={1} value={number} onChange={(e) => setNumber(e.target.value)} />
        </label>
        <label style={{ ...labelStyle, flex: '0 1 7rem' }}>
          Posti
          <input className="sk-input" type="number" min={1} value={seats} onChange={(e) => setSeats(e.target.value)} />
        </label>
        <button className="sk-btn" type="submit" disabled={busy || !number.trim()}>
          + Aggiungi
        </button>
      </form>
      {tables.length > 0 && (
        <div style={listStyle}>
          {tables.map((t) => (
            <div key={t.id} style={listRowStyle}>
              <span>Tavolo {t.table_number}</span>
              <span style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <span style={{ color: 'var(--sk-mid)' }}>{t.seats} posti</span>
                <button type="button" className="sk-btn sk-btn--quiet" onClick={() => void remove(t.id)} disabled={busy}>✕</button>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Schermata: Staff ───────────────────────────────────────────────────────

const STAFF_TARGET = 1;

type StaffRow = { id: number; name: string; role_id: number };

function StaffScreen({ refresh }: { refresh: () => Promise<void> }) {
  const [staff, setStaff] = useState<StaffRow[]>([]);
  const [name, setName] = useState('');
  const [roleId, setRoleId] = useState(1);
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const res = await apiFetch('/api/v1/restaurant/staff');
    if (!res.ok) return;
    const body = (await res.json()) as { staff?: StaffRow[] };
    setStaff(body.staff || []);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function add(e: FormEvent) {
    e.preventDefault();
    if (!/^\d{4}$/.test(pin)) {
      setError('Il PIN deve essere di 4 cifre.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch('/api/v1/restaurant/staff', {
        method: 'POST',
        body: JSON.stringify({ name: name.trim(), role_id: roleId, pin }),
      });
      if (!res.ok) throw new Error(await readError(res));
      setName('');
      setPin('');
      await Promise.all([load(), refresh()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: number) {
    setBusy(true);
    try {
      const res = await apiFetch(`/api/v1/restaurant/staff/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(await readError(res));
      await Promise.all([load(), refresh()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <p style={{ color: 'var(--sk-mid)', fontSize: '0.85rem', marginTop: 0 }}>
        Aggiungi almeno {STAFF_TARGET} membro dello staff. Il PIN a 4 cifre serve per l'accesso rapido in sala.
      </p>
      <p style={{ fontSize: '0.8rem', color: staff.length >= STAFF_TARGET ? 'var(--sk-box-success-fg)' : 'var(--sk-mid)' }}>
        {staff.length}/{STAFF_TARGET} membri staff
      </p>
      <ErrorBox error={error} />
      <form onSubmit={add} style={formRowStyle}>
        <label style={{ ...labelStyle, flex: '1 1 9rem' }}>
          Nome
          <input className="sk-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="es. Marco" />
        </label>
        <label style={{ ...labelStyle, flex: '0 1 7rem' }}>
          Ruolo
          <select className="sk-input" value={roleId} onChange={(e) => setRoleId(Number(e.target.value))}>
            {ROLE_OPTIONS.map((r) => (
              <option key={r.id} value={r.id}>{r.label}</option>
            ))}
          </select>
        </label>
        <label style={{ ...labelStyle, flex: '0 1 6rem' }}>
          PIN
          <input
            className="sk-input"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
            placeholder="1234"
            inputMode="numeric"
            maxLength={4}
          />
        </label>
        <button className="sk-btn" type="submit" disabled={busy || !name.trim() || pin.length !== 4}>
          + Aggiungi
        </button>
      </form>
      {staff.length > 0 && (
        <div style={listStyle}>
          {staff.map((s) => (
            <div key={s.id} style={listRowStyle}>
              <span>{s.name}</span>
              <span style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <span style={{ color: 'var(--sk-mid)' }}>{ROLE_OPTIONS.find((r) => r.id === s.role_id)?.label || `Ruolo ${s.role_id}`}</span>
                <button type="button" className="sk-btn sk-btn--quiet" onClick={() => void remove(s.id)} disabled={busy}>✕</button>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Schermata: Orari ───────────────────────────────────────────────────────

type HourRow = {
  day_of_week: number;
  is_closed: boolean;
  open_time_1: string | null;
  close_time_1: string | null;
  open_time_2: string | null;
  close_time_2: string | null;
};

function defaultHours(): HourRow[] {
  return GIORNI.map((_, i) => ({
    day_of_week: i,
    is_closed: false,
    open_time_1: '09:00',
    close_time_1: '23:00',
    open_time_2: null,
    close_time_2: null,
  }));
}

type HoursVariant = { endpoint: string; centerShape?: boolean };

const HOURS_BUSINESS: HoursVariant = { endpoint: '/api/v1/portale/me/business-hours' };
const HOURS_BEAUTY: HoursVariant = { endpoint: '/api/v1/beauty/center/hours', centerShape: true };
const HOURS_HAIR: HoursVariant = { endpoint: '/api/v1/hair/center-hours', centerShape: true };

function hoursVariantFor(category: string): HoursVariant {
  if (category === 'beauty') return HOURS_BEAUTY;
  if (category === 'hair') return HOURS_HAIR;
  return HOURS_BUSINESS;
}

function HoursScreen({ refresh, variant }: { refresh: () => Promise<void>; variant: HoursVariant }) {
  const [hours, setHours] = useState<HourRow[]>(defaultHours());
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const res = await apiFetch(variant.endpoint);
      if (!res.ok) return;
      const raw = (await res.json()) as unknown;
      const body = Array.isArray(raw) ? raw : (raw as { hours?: unknown[] }).hours;
      if (Array.isArray(body) && body.length > 0) {
        setHours(GIORNI.map((_, i) => {
          const found = body.find((h) => (h as { day_of_week: number }).day_of_week === i) as
            | (HourRow & { is_open?: boolean; open_time?: string; close_time?: string })
            | undefined;
          if (found) {
            return {
              day_of_week: i,
              is_closed: variant.centerShape ? !found.is_open : Boolean(found.is_closed),
              open_time_1: found.open_time_1 ?? found.open_time ?? null,
              close_time_1: found.close_time_1 ?? found.close_time ?? null,
              open_time_2: null,
              close_time_2: null,
            };
          }
          return {
            day_of_week: i, is_closed: true, open_time_1: null, close_time_1: null, open_time_2: null, close_time_2: null,
          };
        }));
      }
    })();
  }, [variant.endpoint, variant.centerShape]);

  function patchDay(day: number, patch: Partial<HourRow>) {
    setSaved(false);
    setHours((prev) => prev.map((h) => (h.day_of_week === day ? { ...h, ...patch } : h)));
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const payload = variant.centerShape
        ? {
            hours: hours.map((h) => ({
              day_of_week: h.day_of_week,
              is_open: !h.is_closed,
              open_time: h.is_closed ? '00:00' : h.open_time_1 || '09:00',
              close_time: h.is_closed ? '00:00' : h.close_time_1 || '18:00',
            })),
          }
        : { hours };
      const res = await apiFetch(variant.endpoint, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await readError(res));
      setSaved(true);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <p style={{ color: 'var(--sk-mid)', fontSize: '0.85rem', marginTop: 0 }}>
        Imposta gli orari di apertura: servono per le prenotazioni e la vetrina pubblica.
      </p>
      <ErrorBox error={error} />
      <form onSubmit={save}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          {hours.map((h) => (
            <div key={h.day_of_week} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <span style={{ flex: '0 0 6.5rem', fontSize: '0.85rem' }}>{GIORNI[h.day_of_week]}</span>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem' }}>
                <input
                  type="checkbox"
                  checked={!h.is_closed}
                  onChange={(e) => patchDay(h.day_of_week, { is_closed: !e.target.checked })}
                />
                Aperto
              </label>
              {!h.is_closed ? (
                <>
                  <input
                    className="sk-input"
                    type="time"
                    style={{ width: 'auto', minHeight: 'auto', padding: '0.25rem 0.5rem' }}
                    value={h.open_time_1 || ''}
                    onChange={(e) => patchDay(h.day_of_week, { open_time_1: e.target.value })}
                  />
                  <span style={{ color: 'var(--sk-mid)', fontSize: '0.8rem' }}>–</span>
                  <input
                    className="sk-input"
                    type="time"
                    style={{ width: 'auto', minHeight: 'auto', padding: '0.25rem 0.5rem' }}
                    value={h.close_time_1 || ''}
                    onChange={(e) => patchDay(h.day_of_week, { close_time_1: e.target.value })}
                  />
                </>
              ) : (
                <span className="sk-muted" style={{ fontSize: '0.8rem' }}>Chiuso</span>
              )}
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.9rem' }}>
          <button className="sk-btn" type="submit" disabled={busy}>
            {busy ? 'Salvataggio…' : 'Salva orari'}
          </button>
          {saved && <span style={{ fontSize: '0.8rem', color: 'var(--sk-box-success-fg)' }}>✓ Orari salvati</span>}
        </div>
      </form>
    </div>
  );
}

// ─── Schermata: Foto ────────────────────────────────────────────────────────

function PhotoScreen({ refresh }: { refresh: () => Promise<void> }) {
  const [coverUrl, setCoverUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const res = await apiFetch('/api/v1/portale/me/public-profile');
      if (!res.ok) return;
      const body = (await res.json()) as { cover_url?: string | null; profile?: { cover_url?: string | null } };
      setCoverUrl(body.cover_url || body.profile?.cover_url || null);
    })();
  }, []);

  async function upload(file: File) {
    setBusy(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await apiFetch('/api/v1/portale/me/upload-cover', { method: 'POST', body: fd });
      if (!res.ok) throw new Error(await readError(res));
      const body = (await res.json()) as { cover_url?: string };
      setCoverUrl(body.cover_url || null);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <p style={{ color: 'var(--sk-mid)', fontSize: '0.85rem', marginTop: 0 }}>
        <strong>Opzionale.</strong> Carica una foto del locale: verrà usata come copertina della home pubblica.
      </p>
      <ErrorBox error={error} />
      {coverUrl && (
        <div style={{ margin: '0.75rem 0' }}>
          <img
            src={coverUrl}
            alt="Copertina locale"
            style={{ maxWidth: '100%', maxHeight: '180px', borderRadius: '0.5rem', objectFit: 'cover', display: 'block' }}
          />
        </div>
      )}
      <label className="sk-btn sk-btn--quiet" style={{ display: 'inline-block', cursor: busy ? 'wait' : 'pointer' }}>
        {busy ? 'Caricamento…' : coverUrl ? 'Cambia foto' : 'Carica foto'}
        <input
          type="file"
          accept="image/*"
          hidden
          disabled={busy}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void upload(f);
            e.target.value = '';
          }}
        />
      </label>
    </div>
  );
}

// ─── Schermata: Primo contenuto (per verticale) ─────────────────────────────

function DoneContent() {
  return (
    <div>
      <div
        style={{
          padding: '0.85rem 1rem',
          borderRadius: '0.5rem',
          background: 'var(--sk-box-success-bg)',
          fontSize: '0.85rem',
          color: 'var(--sk-box-success-fg)',
        }}
      >
        ✓ Primo contenuto creato. Il sistema è operativo: prenotazioni, analytics e passaporto clienti sono attivi.
      </div>
      <p style={{ color: 'var(--sk-mid)', fontSize: '0.8rem', marginTop: '0.75rem' }}>
        Da qui in poi gestisci l'operatività dalle pagine dedicate.
      </p>
    </div>
  );
}

async function fetchList(url: string, key: string): Promise<Array<Record<string, unknown>>> {
  const res = await apiFetch(url);
  if (!res.ok) return [];
  const body = (await res.json()) as Record<string, unknown>;
  const list = body[key] ?? Object.values(body).find((v) => Array.isArray(v));
  return Array.isArray(list) ? (list as Array<Record<string, unknown>>) : [];
}

type Opt = { id: number; label: string };

function pickId(item: Record<string, unknown>, keys: string[]): number | null {
  for (const k of keys) {
    const v = Number(item[k]);
    if (Number.isFinite(v) && v > 0) return v;
  }
  return null;
}

function BeautyBookingForm({ refresh, onCreated }: { refresh: () => Promise<void>; onCreated: () => void }) {
  const [treatments, setTreatments] = useState<Opt[]>([]);
  const [cabins, setCabins] = useState<Opt[]>([]);
  const [operators, setOperators] = useState<Opt[]>([]);
  const [form, setForm] = useState({ treatment_id: '', cabin_id: '', operator_id: '', customer_name: '', date: new Date().toISOString().slice(0, 10), time: '15:00' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const [ts, cs, os] = await Promise.all([
        fetchList('/api/v1/beauty/treatments', 'treatments'),
        fetchList('/api/v1/beauty/cabins', 'cabins'),
        fetchList('/api/v1/beauty/operators', 'operators'),
      ]);
      setTreatments(ts.map((t) => ({ id: pickId(t, ['id', 'treatment_id']) || 0, label: `${t.name} · ${t.duration_min || 60}min` })).filter((o) => o.id));
      setCabins(cs.map((c) => ({ id: pickId(c, ['id', 'cabin_id']) || 0, label: `${c.name}` })).filter((o) => o.id));
      setOperators(os.map((o) => ({ id: pickId(o, ['id', 'operator_id']) || 0, label: `${o.display_name}` })).filter((o) => o.id));
    })();
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const treatment = treatments.find((t) => String(t.id) === form.treatment_id);
    const durationMin = Number(treatment?.label.split('·')[1]?.replace(/\D/g, '')) || 60;
    const start = new Date(`${form.date}T${form.time}:00`);
    if (Number.isNaN(start.getTime())) { setError('Data/ora non valida.'); return; }
    const end = new Date(start.getTime() + durationMin * 60000);
    setBusy(true);
    setError(null);
    try {
      // L'operatore deve avere un turno che copre lo slot: se non ne ha,
      // creiamo un turno full-day per quel giorno (best-effort).
      const shiftsRes = await apiFetch(`/api/v1/beauty/operators/${form.operator_id}/shifts`);
      const shifts = shiftsRes.ok ? ((await shiftsRes.json()) as { shifts?: unknown[] }).shifts || [] : [];
      if (!shifts.length) {
        await apiFetch(`/api/v1/beauty/operators/${form.operator_id}/shifts`, {
          method: 'POST',
          body: JSON.stringify({ day_of_week: (start.getDay() + 6) % 7, start_time: '07:00', end_time: '23:00' }),
        }).catch(() => undefined);
      }
      const res = await apiFetch('/api/v1/beauty/bookings', {
        method: 'POST',
        body: JSON.stringify({
          treatment_id: Number(form.treatment_id),
          cabin_id: Number(form.cabin_id),
          operator_id: Number(form.operator_id),
          start_at: start.toISOString(),
          end_at: end.toISOString(),
          duration_min: durationMin,
          customer_name: form.customer_name.trim() || null,
        }),
      });
      if (!res.ok) throw new Error(await readError(res));
      onCreated();
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const empty = !treatments.length || !cabins.length || !operators.length;
  return (
    <div>
      <p style={{ color: 'var(--sk-mid)', fontSize: '0.85rem', marginTop: 0 }}>
        Crea una prenotazione di test scegliendo trattamento, cabina e operatore.
      </p>
      {empty && !error && (
        <p style={{ color: 'var(--sk-mid)', fontSize: '0.8rem' }}>Completa prima trattamenti, cabine e operatori nelle schermate precedenti.</p>
      )}
      <ErrorBox error={error} />
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
        <div style={formRowStyle}>
          <label style={{ ...labelStyle, flex: '1 1 10rem' }}>Trattamento
            <select className="sk-input" value={form.treatment_id} onChange={(e) => setForm((f) => ({ ...f, treatment_id: e.target.value }))}>
              <option value="">—</option>
              {treatments.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </label>
          <label style={{ ...labelStyle, flex: '0 1 9rem' }}>Cabina
            <select className="sk-input" value={form.cabin_id} onChange={(e) => setForm((f) => ({ ...f, cabin_id: e.target.value }))}>
              <option value="">—</option>
              {cabins.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </label>
          <label style={{ ...labelStyle, flex: '0 1 9rem' }}>Operatore
            <select className="sk-input" value={form.operator_id} onChange={(e) => setForm((f) => ({ ...f, operator_id: e.target.value }))}>
              <option value="">—</option>
              {operators.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </label>
        </div>
        <div style={formRowStyle}>
          <label style={{ ...labelStyle, flex: '1 1 10rem' }}>Nome cliente
            <input className="sk-input" value={form.customer_name} onChange={(e) => setForm((f) => ({ ...f, customer_name: e.target.value }))} placeholder="es. Anna" />
          </label>
          <label style={{ ...labelStyle, flex: '0 1 8rem' }}>Data
            <input className="sk-input" type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} />
          </label>
          <label style={{ ...labelStyle, flex: '0 1 6rem' }}>Ora
            <input className="sk-input" type="time" value={form.time} onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))} />
          </label>
          <button className="sk-btn" type="submit" disabled={busy || !form.treatment_id || !form.cabin_id || !form.operator_id}>
            Crea prenotazione test
          </button>
        </div>
      </form>
    </div>
  );
}

function HairBookingForm({ refresh, onCreated }: { refresh: () => Promise<void>; onCreated: () => void }) {
  const [treatments, setTreatments] = useState<Array<{ id: number; label: string; duration: number }>>([]);
  const [operators, setOperators] = useState<Opt[]>([]);
  const [stations, setStations] = useState<Opt[]>([]);
  const [form, setForm] = useState({ treatment_id: '', operator_id: '', station_id: '', customer_name: '', date: new Date().toISOString().slice(0, 10), time: '15:00' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const [ts, os, ss] = await Promise.all([
        fetchList('/api/v1/hair/treatments', 'treatments'),
        fetchList('/api/v1/hair/operators', 'operators'),
        fetchList('/api/v1/hair/stations', 'stations'),
      ]);
      setTreatments(ts.map((t) => ({ id: pickId(t, ['id', 'treatment_id']) || 0, label: `${t.name}`, duration: Number(t.duration_min) || 60 })).filter((o) => o.id));
      setOperators(os.map((o) => ({ id: pickId(o, ['id', 'operator_id']) || 0, label: `${o.display_name}` })).filter((o) => o.id));
      setStations(ss.map((s) => ({ id: pickId(s, ['id', 'station_id']) || 0, label: `${s.name}` })).filter((o) => o.id));
    })();
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const duration = treatments.find((t) => String(t.id) === form.treatment_id)?.duration || 60;
    const start = new Date(`${form.date}T${form.time}:00`);
    if (Number.isNaN(start.getTime())) { setError('Data/ora non valida.'); return; }
    const end = new Date(start.getTime() + duration * 60000);
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch('/api/v1/hair/bookings', {
        method: 'POST',
        body: JSON.stringify({
          treatment_id: Number(form.treatment_id),
          operator_id: Number(form.operator_id),
          station_id: Number(form.station_id),
          start_time: start.toISOString(),
          end_time: end.toISOString(),
          customer_name: form.customer_name.trim() || null,
        }),
      });
      if (!res.ok) throw new Error(await readError(res));
      onCreated();
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const empty = !treatments.length || !operators.length || !stations.length;
  return (
    <div>
      <p style={{ color: 'var(--sk-mid)', fontSize: '0.85rem', marginTop: 0 }}>
        Crea una prenotazione di test scegliendo trattamento, operatore e postazione.
      </p>
      {empty && !error && (
        <p style={{ color: 'var(--sk-mid)', fontSize: '0.8rem' }}>Completa prima trattamenti, operatori e postazioni nelle schermate precedenti.</p>
      )}
      <ErrorBox error={error} />
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
        <div style={formRowStyle}>
          <label style={{ ...labelStyle, flex: '1 1 10rem' }}>Trattamento
            <select className="sk-input" value={form.treatment_id} onChange={(e) => setForm((f) => ({ ...f, treatment_id: e.target.value }))}>
              <option value="">—</option>
              {treatments.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </label>
          <label style={{ ...labelStyle, flex: '0 1 9rem' }}>Operatore
            <select className="sk-input" value={form.operator_id} onChange={(e) => setForm((f) => ({ ...f, operator_id: e.target.value }))}>
              <option value="">—</option>
              {operators.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </label>
          <label style={{ ...labelStyle, flex: '0 1 9rem' }}>Postazione
            <select className="sk-input" value={form.station_id} onChange={(e) => setForm((f) => ({ ...f, station_id: e.target.value }))}>
              <option value="">—</option>
              {stations.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </label>
        </div>
        <div style={formRowStyle}>
          <label style={{ ...labelStyle, flex: '1 1 10rem' }}>Nome cliente
            <input className="sk-input" value={form.customer_name} onChange={(e) => setForm((f) => ({ ...f, customer_name: e.target.value }))} placeholder="es. Anna" />
          </label>
          <label style={{ ...labelStyle, flex: '0 1 8rem' }}>Data
            <input className="sk-input" type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} />
          </label>
          <label style={{ ...labelStyle, flex: '0 1 6rem' }}>Ora
            <input className="sk-input" type="time" value={form.time} onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))} />
          </label>
          <button className="sk-btn" type="submit" disabled={busy || !form.treatment_id || !form.operator_id || !form.station_id}>
            Crea prenotazione test
          </button>
        </div>
      </form>
    </div>
  );
}

function GymCheckinForm({ refresh, onCreated }: { refresh: () => Promise<void>; onCreated: () => void }) {
  const [members, setMembers] = useState<Opt[]>([]);
  const [memberId, setMemberId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const ms = await fetchList('/api/v1/gym/members', 'members');
      setMembers(ms.map((m) => ({ id: pickId(m, ['id', 'member_id']) || 0, label: `${m.display_name}` })).filter((o) => o.id));
    })();
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch('/api/v1/gym/check-ins', {
        method: 'POST',
        body: JSON.stringify({ member_id: Number(memberId), method: 'manual' }),
      });
      if (!res.ok) throw new Error(await readError(res));
      onCreated();
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <p style={{ color: 'var(--sk-mid)', fontSize: '0.85rem', marginTop: 0 }}>
        Effettua un check-in di test per attivare il flusso operativo (ledger, analytics, passaporto clienti).
      </p>
      {!members.length && !error && (
        <p style={{ color: 'var(--sk-mid)', fontSize: '0.8rem' }}>Registra prima almeno un socio nella schermata Soci.</p>
      )}
      <ErrorBox error={error} />
      <form onSubmit={submit} style={formRowStyle}>
        <label style={{ ...labelStyle, flex: '1 1 12rem' }}>Socio
          <select className="sk-input" value={memberId} onChange={(e) => setMemberId(e.target.value)}>
            <option value="">—</option>
            {members.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
        </label>
        <button className="sk-btn" type="submit" disabled={busy || !memberId}>
          Check-in test
        </button>
      </form>
    </div>
  );
}

function GenericContentForm({ refresh }: { refresh: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  return (
    <div>
      <p style={{ color: 'var(--sk-mid)', fontSize: '0.85rem', marginTop: 0 }}>
        Crea il primo contenuto operativo (prenotazione, ordine o check-in) dalle pagine del tuo verticale,
        poi torna qui: il sistema lo rileva automaticamente.
      </p>
      <button className="sk-btn" disabled={busy} onClick={() => { setBusy(true); void refresh().finally(() => setBusy(false)); }}>
        {busy ? 'Rilevamento…' : 'Rileva ora'}
      </button>
    </div>
  );
}

function FirstContentScreen({ state, refresh, category }: { state: OnboardingState; refresh: () => Promise<void>; category: string }) {
  const item = itemFor(state, 'first_content');
  const [created, setCreated] = useState(false);
  const onCreated = useCallback(() => setCreated(true), []);

  if (item?.done || created) return <DoneContent />;

  switch (category) {
    case 'beauty': return <BeautyBookingForm refresh={refresh} onCreated={onCreated} />;
    case 'hair': return <HairBookingForm refresh={refresh} onCreated={onCreated} />;
    case 'gym': return <GymCheckinForm refresh={refresh} onCreated={onCreated} />;
    case 'restaurant': return <RestaurantReservationForm refresh={refresh} onCreated={onCreated} />;
    default: return <GenericContentForm refresh={refresh} />;
  }
}

function RestaurantReservationForm({ refresh, onCreated }: { refresh: () => Promise<void>; onCreated: () => void }) {
  const [guestName, setGuestName] = useState('');
  const [partySize, setPartySize] = useState('2');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [time, setTime] = useState('20:00');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const reservedAt = new Date(`${date}T${time}:00`);
    if (Number.isNaN(reservedAt.getTime())) {
      setError('Data/ora non valida.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch('/api/v1/restaurant/reservations', {
        method: 'POST',
        body: JSON.stringify({
          guest_name: guestName.trim(),
          party_size: Number(partySize) || 2,
          reserved_at: reservedAt.toISOString(),
        }),
      });
      if (!res.ok) throw new Error(await readError(res));
      onCreated();
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <p style={{ color: 'var(--sk-mid)', fontSize: '0.85rem', marginTop: 0 }}>
        Crea una prenotazione di test per attivare il flusso operativo (ledger, analytics, passaporto clienti).
      </p>
      <ErrorBox error={error} />
      <form onSubmit={submit} style={formRowStyle}>
        <label style={{ ...labelStyle, flex: '1 1 10rem' }}>
          Nome cliente
          <input className="sk-input" value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder="es. Rossi" />
        </label>
        <label style={{ ...labelStyle, flex: '0 1 5rem' }}>
          Coperti
          <input className="sk-input" type="number" min={1} value={partySize} onChange={(e) => setPartySize(e.target.value)} />
        </label>
        <label style={{ ...labelStyle, flex: '0 1 8rem' }}>
          Data
          <input className="sk-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <label style={{ ...labelStyle, flex: '0 1 6rem' }}>
          Ora
          <input className="sk-input" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
        </label>
        <button className="sk-btn" type="submit" disabled={busy || !guestName.trim()}>
          Crea prenotazione test
        </button>
      </form>
      <p style={{ color: 'var(--sk-mid)', fontSize: '0.75rem', marginTop: '0.75rem' }}>
        In alternativa: crea un ordine dalla pagina Ordini — il sistema lo rileva automaticamente.
      </p>
    </div>
  );
}

// ─── Schermata: Riepilogo ───────────────────────────────────────────────────

function SummaryScreen({ state, onJump }: { state: OnboardingState; onJump: (itemKey: string) => void }) {
  const required = state.checklist.filter((i) => !i.optional);
  const optional = state.checklist.filter((i) => i.optional);
  const missing = required.filter((i) => !i.done);

  return (
    <div>
      <p style={{ color: 'var(--sk-mid)', fontSize: '0.85rem', marginTop: 0 }}>
        {missing.length === 0
          ? 'Tutto pronto: puoi completare la configurazione.'
          : `Mancano ${missing.length} elementi obbligatori. Clicca su un elemento per completarlo, o aprilo dalla pagina dedicata.`}
      </p>
      <div style={listStyle}>
        {required.map((item) => (
          <div
            key={item.key}
            onClick={!item.done ? () => onJump(item.key) : undefined}
            role={!item.done ? 'button' : undefined}
            style={{
              ...listRowStyle,
              background: item.done ? 'var(--sk-box-success-bg)' : 'var(--sk-box-neutral-bg)',
              cursor: item.done ? 'default' : 'pointer',
            }}
          >
            <span style={{ color: item.done ? 'var(--sk-box-success-fg)' : 'var(--sk-ink)' }}>{item.label}</span>
            <span style={{ fontSize: '0.8rem', color: item.done ? 'var(--sk-box-success-fg)' : 'var(--sk-accent)' }}>
              {item.done ? '✓' : 'da fare →'}
            </span>
          </div>
        ))}
      </div>
      {optional.length > 0 && (
        <>
          <p style={{ color: 'var(--sk-mid)', fontSize: '0.75rem', margin: '0.9rem 0 0.35rem', textTransform: 'uppercase', fontWeight: 600 }}>
            Opzionali
          </p>
          <div style={listStyle}>
            {optional.map((item) => (
              <div key={item.key} style={listRowStyle}>
                <span style={{ color: 'var(--sk-mid)', textDecoration: item.done ? 'line-through' : 'none' }}>{item.label}</span>
                <span style={{ fontSize: '0.8rem', color: item.done ? 'var(--sk-box-success-fg)' : 'var(--sk-mid)' }}>
                  {item.done ? '✓' : '—'}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Wizard shell ───────────────────────────────────────────────────────────

export function SetupWizard({
  state,
  onClose,
  onUpdate,
}: {
  state: OnboardingState;
  onClose: () => void;
  onUpdate: (s: OnboardingState) => void;
}) {
  const { entitlements } = useAuth();
  const category = String(entitlements?.category || '');
  const SCREENS = useMemo(() => screensFor(category), [category]);
  const modules = useMemo(
    () => new Set((entitlements?.modules || []).map((m) => m.toLowerCase())),
    [entitlements],
  );
  const [idx, setIdx] = useState(() => {
    const first = SCREENS.findIndex((s) => s.itemKey && !itemFor(state, s.itemKey)?.done);
    return first >= 0 ? first : SCREENS.length - 1;
  });
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    const res = await apiFetch('/api/v1/tenant/onboarding/auto-detect', { method: 'POST' });
    if (res.ok) onUpdate((await res.json()) as OnboardingState);
  }, [onUpdate]);

  async function persistStep(step: number) {
    await apiFetch('/api/v1/tenant/onboarding/step', {
      method: 'PATCH',
      body: JSON.stringify({ step }),
    }).catch(() => undefined);
  }

  const screen = SCREENS[idx];
  const item = itemFor(state, screen.itemKey);
  const locked = Boolean(screen.module && !modules.has(screen.module));
  const allRequiredDone = state.checklist.every((i) => i.optional || i.done);

  function goTo(nextIdx: number) {
    const clamped = Math.max(0, Math.min(nextIdx, SCREENS.length - 1));
    setIdx(clamped);
    // Schermate dati → step 3 "configurazione"; primo contenuto/riepilogo → step 5
    void persistStep(clamped >= SCREENS.length - 2 ? 5 : 3);
  }

  async function complete() {
    setBusy(true);
    try {
      const res = await apiFetch('/api/v1/tenant/onboarding/complete', { method: 'POST' });
      if (res.ok) onClose();
    } finally {
      setBusy(false);
    }
  }

  const screenContent = (() => {
    if (locked) return <LockedNotice moduleName={screen.moduleName || screen.module || ''} />;
    switch (screen.id) {
      case 'fiscal': return <FiscalScreen refresh={refresh} />;
      case 'products': return <ProductsScreen refresh={refresh} />;
      case 'tables': return <TablesScreen refresh={refresh} />;
      case 'staff': return <StaffScreen refresh={refresh} />;
      case 'treatments': return <QuickAddScreen cfg={category === 'hair' ? HAIR_TREATMENTS_CFG : BEAUTY_TREATMENTS_CFG} refresh={refresh} />;
      case 'cabins': return <QuickAddScreen cfg={BEAUTY_CABINS_CFG} refresh={refresh} />;
      case 'operators': return <QuickAddScreen cfg={category === 'hair' ? HAIR_OPERATORS_CFG : BEAUTY_OPERATORS_CFG} refresh={refresh} />;
      case 'stations': return <QuickAddScreen cfg={HAIR_STATIONS_CFG} refresh={refresh} />;
      case 'memberships': return <QuickAddScreen cfg={GYM_MEMBERSHIPS_CFG} refresh={refresh} />;
      case 'coaches': return <QuickAddScreen cfg={GYM_COACHES_CFG} refresh={refresh} />;
      case 'members': return <QuickAddScreen cfg={GYM_MEMBERS_CFG} refresh={refresh} />;
      case 'hours': return <HoursScreen refresh={refresh} variant={hoursVariantFor(category)} />;
      case 'photo': return <PhotoScreen refresh={refresh} />;
      case 'first_content': return <FirstContentScreen state={state} refresh={refresh} category={category} />;
      case 'summary': return (
        <SummaryScreen
          state={state}
          onJump={(key) => {
            const i = SCREENS.findIndex((s) => s.itemKey === key);
            if (i >= 0) goTo(i);
          }}
        />
      );
    }
  })();

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
      }}
      onClick={onClose}
    >
      <div
        className="sk-saas-card"
        style={{ maxWidth: '640px', width: '100%', maxHeight: '90vh', overflow: 'auto', padding: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '1.25rem 1.5rem',
            borderBottom: '1px solid var(--sk-border)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem',
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>Configurazione guidata</h2>
            <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.8rem', color: 'var(--sk-mid)' }}>
              {idx + 1} di {SCREENS.length}: {screen.title}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <DoneTag item={item} detail={item?.optional ? 'opzionale' : 'da fare'} />
            <button className="sk-btn sk-btn--quiet" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* Progress */}
        <div style={{ display: 'flex', padding: '0.75rem 1.5rem', borderBottom: '1px solid var(--sk-border)', gap: '0.25rem' }}>
          {SCREENS.map((s, i) => (
            <div
              key={s.id}
              style={{
                flex: 1, height: '4px', borderRadius: '2px',
                background: i <= idx ? 'var(--sk-accent)' : 'var(--sk-border)',
                transition: 'background 0.2s',
              }}
            />
          ))}
        </div>

        {/* Content */}
        <div style={{ padding: '1.5rem' }} key={screen.id}>
          {screenContent}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '1rem 1.5rem',
            borderTop: '1px solid var(--sk-border)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}
        >
          <button className="sk-btn sk-btn--quiet" onClick={() => goTo(idx - 1)} disabled={idx === 0 || busy}>
            ← Indietro
          </button>
          {screen.id !== 'summary' ? (
            <button className="sk-btn" onClick={() => goTo(idx + 1)} disabled={busy}>
              Avanti →
            </button>
          ) : (
            <button
              className="sk-btn"
              onClick={complete}
              disabled={busy || !allRequiredDone}
              style={!allRequiredDone ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
            >
              {allRequiredDone ? '✓ Completa configurazione' : 'Completa gli elementi obbligatori'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default SetupWizard;
